/**
 * sourceModel — the matter's documents, as ONE list.
 *
 * The Documents tab used to be six-to-ten sibling tabs (Case Files, Laws,
 * Precedents, Preparatory Works, Regulations, Agency Decisions, plus a tab per
 * connected data source), each with its own toolbar, its own empty state and
 * its own idea of what a row looked like. The corpus a document happens to sit
 * in is a detail; the question a lawyer actually asks is "is this OUR paper, or
 * is this the law we are arguing from?"
 *
 * So the payload is flattened here into a single `MatterSource[]`, and each
 * item carries three derived facts the UI sorts on:
 *
 *   section   case_files | legal_authority   — the two things the tab shows
 *   corpus    legislation, case_law, …       — a FILTER CHIP, not a tab
 *   origin    uploaded | ai_found            — visible on every row
 *
 * Everything is pure so the derivation is testable without a DOM: this is
 * where the "uploaded law disappeared" bug lived, and a bug in a `useMemo`
 * inside a 2000-line component is not reachable by a test.
 */

/**
 * Document types the backend re-routes: a file uploaded as one of these is
 * written into `laws.json` / `precedents.json`, NOT into `user_documents`'
 * case-file view.
 *
 * THIS IS THE DISAPPEARING UPLOAD. The old tab filtered these out of Case
 * Files and showed them only under Laws/Precedents, so a user who uploaded a
 * contract and tagged it "law" watched it vanish. In this model it is still
 * routed the same way — but it lands in Legal authority wearing an "Uploaded"
 * pill, and the upload handler says where it went.
 */
export const LEGAL_SOURCE_DOC_TYPES = ['law', 'legislation', 'case', 'precedent'];

export type SourceSection = 'case_files' | 'legal_authority';

export type SourceOrigin = 'uploaded' | 'ai_found';

export type SourceCorpus =
  | 'case_file'
  | 'legislation'
  | 'case_law'
  | 'preparatory_works'
  | 'regulations'
  | 'agency_decisions'
  | 'data_sources';

/** The chips in Legal authority, in order. `all` is prepended by the UI. */
export const AUTHORITY_CORPORA: SourceCorpus[] = [
  'legislation',
  'case_law',
  'preparatory_works',
  'regulations',
  'agency_decisions',
  'data_sources',
];

/** Untranslated labels — the caller runs them through `t`. */
export const CORPUS_LABELS: Record<SourceCorpus, string> = {
  case_file: 'Case file',
  legislation: 'Legislation',
  case_law: 'Case law',
  preparatory_works: 'Prep works',
  regulations: 'Regulations',
  agency_decisions: 'Agency',
  data_sources: 'Data sources',
};

/**
 * Which `/matters/available-indexes` category feeds each corpus.
 *
 * Used to answer "does this jurisdiction even have preparatory works?" so an
 * empty chip can say "not available here" instead of looking broken.
 */
export const CORPUS_INDEX_CATEGORY: Partial<Record<SourceCorpus, string>> = {
  legislation: 'legislation',
  case_law: 'case_law',
  preparatory_works: 'preparatory_works',
  regulations: 'regulation',
  agency_decisions: 'agency_decisions',
};

export interface MatterSource {
  /** Stable row key and the id every delete path expects. */
  id: string;
  source_id?: string;
  name: string;
  section: SourceSection;
  corpus: SourceCorpus;
  origin: SourceOrigin;
  /**
   * The legacy discriminator the delete + reader call sites still switch on
   * (`document` deletes by blob path, everything else by source id).
   * Unchanged on purpose — this rebuild is the view, not the API.
   */
  _sourceType: 'document' | 'law' | 'case' | 'file' | 'datasource' | string;
  _datasourceName?: string;
  documentType?: string;
  blobPath?: string;
  /**
   * The storage container `blobPath` lives in. Empty means the matter's own
   * default; a DATASOURCE source names `service-providers`, and the streamer
   * cannot find the blob without it.
   */
  container?: string;
  contentType?: string;
  url?: string;
  size?: number;
  lastModified?: string;
  content?: string;
  summary?: string;
  description?: string;
  keywords?: string[] | string;
  country?: string;
  language?: string;
  justification?: string;
  relevanceScore?: number;
  isTargeted?: boolean;
  evaluationStatus?: string;
  relevantSections?: any;
  encrypted?: boolean;
  encryptionFormat?: string;
  [key: string]: any;
}

/* ------------------------------------------------------------------ */
/*  Origin                                                             */
/* ------------------------------------------------------------------ */

/**
 * Uploaded, or found by the AI?
 *
 * The API stamps `origin: "user_upload"` on anything a person uploaded and
 * writes NO origin field on anything retrieval produced, so the rule is a
 * single equality check — reliable against today's data, with no backend
 * change needed to start.
 *
 * `fallback` covers the one case the rule cannot: a row sitting in the
 * `user_documents` bucket IS an upload by definition, even on older records
 * written before the stamp existed. It is never used to promote a retrieved
 * source to "Uploaded" — only to keep a genuine upload from being mislabelled.
 */
export function deriveOrigin(raw: any, fallback: SourceOrigin = 'ai_found'): SourceOrigin {
  if (raw?.origin === 'user_upload') return 'uploaded';
  if (raw?.origin) return 'ai_found';
  return fallback;
}

/* ------------------------------------------------------------------ */
/*  Normalisation                                                      */
/* ------------------------------------------------------------------ */

/** The payload nests documents inconsistently; every reader goes through this. */
const asArray = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.documents)) return raw.documents;
  if (Array.isArray(raw.citations)) return raw.citations;
  return [];
};

/** Relevant sections arrive as strings or as `{section_name, section_content}`. */
const flattenSections = (sections: any): any => {
  if (!Array.isArray(sections)) return sections;
  return sections.map((s: any) =>
    typeof s === 'string'
      ? s
      : s?.section_name
      ? `${s.section_name}: ${s.section_content || ''}`
      : s?.section_content || String(s)
  );
};

/** An authority document (law, precedent, prep work, regulation, agency, DS). */
function mapAuthorityDoc(
  doc: any,
  corpus: SourceCorpus,
  sourceType: string,
  datasourceName?: string
): MatterSource {
  return {
    ...doc,
    name: doc.title || doc.name || doc.filename || doc.file_name || 'Untitled',
    url: doc.external_url || doc.cdn_url || '',
    contentType:
      doc.content_type ||
      doc.file_type ||
      (doc.blob_path?.endsWith('.pdf') || doc.file_path?.endsWith('.pdf')
        ? 'application/pdf'
        : 'application/octet-stream'),
    lastModified: doc.created_at || doc.uploaded_at || doc.last_modified || '',
    size: doc.characters || doc.file_size || doc.size || doc.content?.length || 0,
    content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
    id: String(doc.id || doc.file_uuid || doc.blob_path || doc.title || ''),
    blobPath: doc.blob_path || doc.file_path,
    container: doc.container || '',
    relevanceScore: doc.relevance_score,
    summary: doc.summary,
    description: doc.document_description,
    keywords: doc.keywords,
    practicalApplication: doc.practical_application,
    country: doc.country,
    language: doc.language,
    searchScore: doc.search_score,
    rerankerScore: doc.search_reranker_score,
    justification: doc.justification,
    // Deterministic "cited" flag from the retriever (verified citation match).
    isTargeted: doc.is_targeted ?? false,
    targetedKind: doc.targeted_kind,
    evaluationStatus: doc.evaluation_status,
    relevantSections: flattenSections(doc.relevant_sections),
    section: 'legal_authority',
    corpus,
    origin: deriveOrigin(doc),
    _sourceType: sourceType,
    ...(datasourceName ? { _datasourceName: datasourceName } : {}),
  };
}

/** A case file — something a person uploaded about the matter itself. */
function mapCaseFile(doc: any): MatterSource {
  return {
    ...doc,
    name: doc.filename || doc.title || 'Untitled',
    url: doc.blob_url || '',
    contentType: doc.content_type || 'application/octet-stream',
    lastModified: doc.uploaded_at || doc.created_at || '',
    size: doc.size || 0,
    content: doc.summary || doc.content_preview || doc.snippet || doc.document_description || doc.content,
    // Documents are deleted BY BLOB PATH, so that is their id here.
    id: String(doc.blob_path || doc.id || ''),
    encrypted: doc.encrypted || false,
    encryptionFormat: doc.encryption_format,
    blobPath: doc.blob_path,
    container: doc.container || '',
    type: 'document',
    documentType: doc.document_type || 'general',
    relevanceScore: doc.relevance_score,
    summary: doc.summary,
    description: doc.document_description,
    keywords: doc.keywords,
    practicalApplication: doc.practical_application,
    country: doc.country,
    language: doc.language,
    justification: doc.justification,
    relevantSections: flattenSections(doc.relevant_sections),
    section: 'case_files',
    corpus: 'case_file',
    // A row in the uploads bucket is an upload even if the stamp predates it.
    origin: deriveOrigin(doc, 'uploaded'),
    _sourceType: 'document',
  };
}

export interface NormaliseOptions {
  /** Case law is hidden entirely where the jurisdiction has no such corpus. */
  showCases?: boolean;
}

/**
 * Flatten the whole `/matters/sources` payload into one list.
 *
 * Note what is NOT dropped: a `user_documents` row with a legal document_type
 * is skipped here only because the backend has ALREADY copied it into
 * `laws`/`precedents`. Including it in both places would double-count it; the
 * copy in Legal authority carries `origin: user_upload`, so the upload is
 * still visible and still marked as ours.
 */
export function normaliseSources(sources: any, options: NormaliseOptions = {}): MatterSource[] {
  if (!sources) return [];
  const { showCases = true } = options;
  const out: MatterSource[] = [];

  asArray(sources.user_documents)
    .filter((doc: any) => !LEGAL_SOURCE_DOC_TYPES.includes(doc.document_type))
    .forEach((doc: any) => out.push(mapCaseFile(doc)));

  asArray(sources.laws ?? sources.retrieved_laws).forEach((doc: any) =>
    out.push(mapAuthorityDoc(doc, 'legislation', 'law'))
  );

  if (showCases) {
    asArray(sources.precedents ?? sources.cases ?? sources.retrieved_cases).forEach((doc: any) =>
      out.push(mapAuthorityDoc(doc, 'case_law', 'case'))
    );
  }

  asArray(sources.preparatory_works).forEach((doc: any) =>
    out.push(mapAuthorityDoc(doc, 'preparatory_works', 'preparatory_work'))
  );
  asArray(sources.regulations).forEach((doc: any) =>
    out.push(mapAuthorityDoc(doc, 'regulations', 'regulation'))
  );
  asArray(sources.agency_decisions).forEach((doc: any) =>
    out.push(mapAuthorityDoc(doc, 'agency_decisions', 'agency_decision'))
  );

  (sources.custom_datasources || []).forEach((ds: any) => {
    asArray(ds.documents).forEach((doc: any) =>
      out.push(
        mapAuthorityDoc(doc, 'data_sources', 'datasource', ds.datasource_name || 'Data Source')
      )
    );
  });

  return out;
}

/* ------------------------------------------------------------------ */
/*  Search, filter, sort                                               */
/* ------------------------------------------------------------------ */

const haystack = (item: MatterSource): string => {
  const keywords = Array.isArray(item.keywords)
    ? item.keywords.join(' ')
    : typeof item.keywords === 'string'
    ? item.keywords
    : '';
  // Name, what it says, and what it is tagged with. The old box searched the
  // filename only, which is the least informative of the three for a law
  // whose "name" is a section number.
  return [item.name, item.summary, item.description, keywords].filter(Boolean).join(' ').toLowerCase();
};

export function matchesSearch(item: MatterSource, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return haystack(item).includes(needle);
}

export interface SourceFilter {
  section: SourceSection;
  /** `null` = the "All" chip. Ignored outside Legal authority. */
  corpus?: SourceCorpus | null;
  /** `null` = both. */
  origin?: SourceOrigin | null;
  search?: string;
}

export function filterSources(items: MatterSource[], filter: SourceFilter): MatterSource[] {
  return items.filter((item) => {
    if (item.section !== filter.section) return false;
    if (filter.section === 'legal_authority' && filter.corpus && item.corpus !== filter.corpus) {
      return false;
    }
    if (filter.origin && item.origin !== filter.origin) return false;
    return matchesSearch(item, filter.search || '');
  });
}

/**
 * Relevance first where there is any, then name.
 *
 * Authority is ranked (the whole point of retrieval is an ordering); case
 * files are not, so they read alphabetically like a folder.
 */
export function sortSources(items: MatterSource[]): MatterSource[] {
  return [...items].sort((a, b) => {
    const aScore = a.relevanceScore || 0;
    const bScore = b.relevanceScore || 0;
    if (aScore !== bScore) return bScore - aScore;
    return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true });
  });
}

/* ------------------------------------------------------------------ */
/*  Counts                                                             */
/* ------------------------------------------------------------------ */

/**
 * The counts the Documents tab actually shows, from the same normalisation
 * it renders — options included.
 *
 * THE 12-vs-2 BUG. Two callers counted authorities by calling
 * `normaliseSources(sources)` with NO options, while the tab calls it with
 * `{ showCases: hasCasesSupport(jurisdiction) }`. On a jurisdiction with no
 * case-law corpus the tab drops `case_law` entirely and the counters kept it,
 * so a matter with two pieces of legislation reported twelve authorities —
 * the extra ten being entries from the cases bucket, which `asArray` will
 * read out of `retrieved_cases.citations` when the bucket is a
 * `{ response, citations }` object rather than a document list.
 *
 * Counting through one helper that takes the same options is what stops the
 * two from drifting again.
 */
export function countMatterSources(
  sources: any,
  options: NormaliseOptions = {}
): { documents: number; authorities: number } {
  const bySection = countBySection(normaliseSources(sources, options));
  return { documents: bySection.case_files, authorities: bySection.legal_authority };
}

export function countBySection(items: MatterSource[]): Record<SourceSection, number> {
  return {
    case_files: items.filter((i) => i.section === 'case_files').length,
    legal_authority: items.filter((i) => i.section === 'legal_authority').length,
  };
}

/**
 * Chip counts for Legal authority, honouring the search and origin filter so
 * a chip never promises rows the current filter would hide.
 */
export function countByCorpus(
  items: MatterSource[],
  filter: Omit<SourceFilter, 'corpus' | 'section'> = {}
): Record<SourceCorpus, number> {
  const counts = {
    case_file: 0,
    legislation: 0,
    case_law: 0,
    preparatory_works: 0,
    regulations: 0,
    agency_decisions: 0,
    data_sources: 0,
  } as Record<SourceCorpus, number>;

  filterSources(items, { ...filter, section: 'legal_authority' }).forEach((item) => {
    counts[item.corpus] += 1;
  });
  return counts;
}

export function countByOrigin(
  items: MatterSource[],
  filter: SourceFilter
): Record<SourceOrigin, number> {
  const scoped = filterSources(items, { ...filter, origin: null });
  return {
    uploaded: scoped.filter((i) => i.origin === 'uploaded').length,
    ai_found: scoped.filter((i) => i.origin === 'ai_found').length,
  };
}

/**
 * Is this corpus offered at all for the matter's jurisdiction?
 *
 * A corpus with documents is available by definition. Otherwise it depends on
 * whether the backend publishes a search index for it — which is what lets an
 * empty chip say "not available here" rather than looking like a broken tab.
 */
export function isCorpusAvailable(
  corpus: SourceCorpus,
  indexCategories: Set<string>,
  count: number
): boolean {
  if (count > 0) return true;
  // Connected data sources are not jurisdiction-scoped: the chip is available
  // whenever the org has connected one, which is exactly "count > 0" above.
  if (corpus === 'data_sources') return false;
  const category = CORPUS_INDEX_CATEGORY[corpus];
  return category ? indexCategories.has(category) : false;
}
